'use client';

import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import ArrowDropDownSmall from 'icons/lib/arrow_drop_down_small.svg';
import clsx from 'classnames';
import { AnimateHeight } from '../../../helpers';

import { AccordionProps } from './types';
import styles from './Accordion.module.scss';
import { Button } from '../button';

export const Accordion = forwardRef(
	(
		{
			children,
			contentClassNames,
			expanded = false,
			containerClassNames,
			headerClassNames,
			headerExpandedClassNames,
			heading,
			pressOnlyArrow = false,
			id = 'accordion-0',
			toggleExpand,
			onHeadingPress,
		}: AccordionProps,
		ref: ForwardedRef<HTMLDivElement>
	) => {
		const [_expanded, setExpanded] = useState<boolean>(expanded);

		const wrapperClasses = clsx(styles.accordionWrapper, containerClassNames);
		const headerClasses = clsx(
			styles.accordionHeader,
			{
				[styles[`accordionHeader--opened`]]: _expanded,
			},
			headerClassNames,
			headerExpandedClassNames
				? {
						headerExpandedClassNames: expanded,
				  }
				: undefined
		);

		const contentClasses = clsx(styles.accordionContent, {
			[styles[`accordionContent--opened`]]: _expanded,
		});

		const onHeadingClick = () => {
			const newValue = !_expanded;
			toggleAccordion(newValue);
			onHeadingPress?.(id);
		};

		const toggleAccordion = (value: boolean) => {
			setExpanded(value);
		};

		useEffect(() => {
			if (!toggleExpand) {
				return;
			}
			toggleAccordion(true);
			onHeadingPress?.(id);
		}, [toggleExpand]);

		return (
			<div className={wrapperClasses} ref={ref}>
				<h4 className={headerClasses} onClick={pressOnlyArrow ? undefined : onHeadingClick}>
					<span>{heading}</span>
					<Button variant="custom" onPress={pressOnlyArrow ? onHeadingClick : undefined}>
						<ArrowDropDownSmall />
					</Button>
				</h4>
				<AnimateHeight duration={500} height={_expanded ? 'auto' : 0}>
					<div className={contentClassNames}>{children}</div>
				</AnimateHeight>
			</div>
		);
	}
);

Accordion.displayName = 'Accordion';
